import type { DatiSensoreDinamicoDTO } from '@/models/dto/plc/dati-sensore-dinamico-dto'
import type { DatiSensoreStaticoDTO } from '@/models/dto/plc/dati-sensore-statico-dto'
import type { FotoDTO } from '@/models/dto/plc/foto-dto'
import type { FotoPaginationDTO } from '@/models/dto/plc/foto-pagination-dto'
import type { PLCBaseDTO } from '@/models/dto/plc/plc-base-dto'
import type { PLCDTO } from '@/models/dto/plc/plc-dto'
import type { PLCPaginationDTO } from '@/models/dto/plc/plc-pagination-dto'
import type { SensorePLCDTO } from '@/models/dto/plc/sensore-plc-dto'
import { mapToBasePLC } from '@/models/mappers/plc/base-plc-mapper'
import { mapToDynamicSensorReadings } from '@/models/mappers/plc/dynamic-sensor-readings-mapper'
import { mapToPhoto } from '@/models/mappers/plc/photo-mapper'
import { mapToPhotosWithPagination } from '@/models/mappers/plc/photos-mapper'
import { mapToPLC, mapToPlcDTO, mapToDevicesDTO, mapToDeviceDTO } from '@/models/mappers/plc/plc-mapper'
import { mapToPLCs } from '@/models/mappers/plc/plcs-mapper'
import { mapToSensorConfiguration } from '@/models/mappers/plc/sensor-configuration-mapper'
import { mapToStaticSensorReadings } from '@/models/mappers/plc/static-sensor-readings-mapper'
import type { AggretationStrategy } from '@/models/types/plc/aggregation-strategy'
import type { BasePLC } from '@/models/types/plc/base-plc'
import type { DynamicSensorReadings } from '@/models/types/plc/dynamic-sensor-readings'
import type { Photo } from '@/models/types/plc/photo'
import type { Device, Devices } from '@/models/types/plc/devices'
import type { PhotosWithPagination } from '@/models/types/plc/photos-with-pagination'
import type { PLC } from '@/models/types/plc/plc'
import type { PLCsWithPagination } from '@/models/types/plc/plcs-with-pagination'
import type { Sensor } from '@/models/types/plc/sensor'
import type { SensorConfiguration } from '@/models/types/plc/sensor-configuration'
import type { StaticSensorReadings } from '@/models/types/plc/static-sensor-readings'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import { makeRequest, type MakeRequestOptions } from './make-request'
import { DeviceDTO, DevicesDTO } from '@/models/dto/plc/devices-dto'
import { promises } from 'dns'

type PlcParams = {
    order?: OrderType
    sort?: keyof PLCDTO
    search?: string
    page?: string
} & Partial<Record<keyof PLC, string>>

type SensorParams = {
    from?: string
    to?: string
    strategy?: AggretationStrategy
    onlyLastValue?: boolean
} & Partial<Record<keyof Sensor, string>>

type PhotoParams = {
    order?: OrderType
    sort?: keyof PLCDTO
    search?: string
    page?: string
    timestampFrom?: string
    timestampTo?: string
} & Partial<Record<keyof Photo, string>>

type PLCCrudType = () => {
    getOne: (id: number, options?: MakeRequestOptions) => Promise<PLC>
    getOneByFacilityId: (id: number, options?: MakeRequestOptions) => Promise<PLC>
    getAll: (options?: MakeRequestOptions) => Promise<BasePLC[]>
    getMany: (
        itemsPerPage?: string,
        params?: PlcParams,
        onlyEnabled?: boolean,
        options?: MakeRequestOptions,
    ) => Promise<PLCsWithPagination>
    put: (PLC: PLC, options?: MakeRequestOptions) => Promise<PLC>
    delete: (id: number, options?: MakeRequestOptions) => Promise<void>
    getDynamicSensorReadings: (
        plcId: number,
        sensorId: number,
        params?: SensorParams,
        options?: MakeRequestOptions,
    ) => Promise<DynamicSensorReadings>
    getStaticSensorReadings: (
        plcId: number,
        sensorId: number,
        params?: SensorParams,
        options?: MakeRequestOptions,
    ) => Promise<StaticSensorReadings>
    getDigitalSensorReadings: (
        plcId: number,
        sensorId: number,
        params?: SensorParams,
        options?: MakeRequestOptions,
    ) => Promise<StaticSensorReadings>
    getSensorConfiguration: (
        sensorId: number,
        sensorName: string,
        options?: MakeRequestOptions,
    ) => Promise<SensorConfiguration>
    getNewPhoto: (plcId: number, options?: MakeRequestOptions) => Promise<Photo>
    getLastPhoto: (pldId: number, options?: MakeRequestOptions) => Promise<Photo>
    getManyPhotos: (
        pldId: number,
        itemsPerPage?: string,
        params?: PhotoParams,
        options?: MakeRequestOptions,
    ) => Promise<PhotosWithPagination>,
    getDevices: (
        plcId: number,
        options?: MakeRequestOptions
    ) => Promise<Devices>
    putDevice: (
        plcId: number,
        deviceId: string,
        command: string,
        options?: MakeRequestOptions
    ) => Promise<Device>
}

const PLC_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL}/api/v1/PLC`
        : `${location.origin}${process.env.NEXT_PUBLIC_BASEPATH}/api/v1/PLC`
const PLC_TAG = 'PLC'
const PHOTO_TAG = 'Photo'

const plcCRUD: PLCCrudType = () => ({
    getOne: async (id, options) => {
        const url = new URL(`${PLC_URL}/${id.toString()}`)
        const plcDTO = await makeRequest<PLCDTO>(url, { method: 'GET', next: { tags: [PLC_TAG] }, ...options })
        const plc = mapToPLC(plcDTO)
        return plc
    },
    getOneByFacilityId: async (id, options) => {
        const url = new URL(`${PLC_URL}/impianto/${id.toString()}`)
        const plcDTO = await makeRequest<PLCDTO>(url, { method: 'GET', next: { tags: [PLC_TAG] }, ...options })
        const plc = mapToPLC(plcDTO)
        return plc
    },
    getAll: async (options) => {
        const url = new URL(PLC_URL)
        const plcDTOs = await makeRequest<PLCBaseDTO[]>(url, { method: 'GET', next: { tags: [PLC_TAG] }, ...options })
        const plcs = plcDTOs.map((element) => mapToBasePLC(element))
        return plcs
    },
    getMany: async (itemsPerPage, params, onlyEnabled = false, options) => {
        const url = new URL(`${PLC_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        if (params?.page) url.searchParams.append('PageIndex', params.page)
        if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
        if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))

        if (onlyEnabled) url.searchParams.append('OnlyActive', 'true')

        if (params?.search) url.searchParams.append('Filters[search]', params.search)

        if (params?.enabled) url.searchParams.append('Filter[Attivo]', params.enabled)
        if (params?.name) url.searchParams.append('Filters[Nome]', params.name)
        if (params?.status) url.searchParams.append('Filters[Stato]', params.status)
        if (params?.version) url.searchParams.append('Filters[Versione]', params.version)

        const plcsPaginationDTO = await makeRequest<PLCPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG] },
            ...options,
        })
        const plcsPagination = mapToPLCs(plcsPaginationDTO)
        return plcsPagination
    },
    put: async (plc, options) => {
        const plcDTO = mapToPlcDTO(plc)
        const url = new URL(PLC_URL)
        const plcsDTOResponse = await makeRequest<PLCDTO>(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(plcDTO),
            ...options,
        })
        const newPLC = mapToPLC(plcsDTOResponse)
        return newPLC
    },
    delete: async (id, options) => {
        const url = new URL(`${PLC_URL}/${id.toString()}`)
        await makeRequest(url, { method: 'DELETE', ...options })
    },
    getSensorConfiguration: async (sensorId, sensorName, options) => {
        const url = new URL(`${PLC_URL}/sensore/${sensorId.toString()}/nome/${sensorName}/configurazione`)
        const sensorePLCDTO = await makeRequest<SensorePLCDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG] },
            ...options,
        })
        const sensorConfiguration = mapToSensorConfiguration(sensorePLCDTO)
        return sensorConfiguration
    },
    getDynamicSensorReadings: async (plcId, sensorId, params, options) => {
        const { from, to, strategy, onlyLastValue } = params || {}
        const url = new URL(`${PLC_URL}/${plcId.toString()}/SensoreDinamico/${sensorId.toString()}`)
        if (from) url.searchParams.append('timestampFrom', from)
        if (to) url.searchParams.append('timestampTo', to)
        if (strategy) url.searchParams.append('aggregationStrategy', strategy.toString())
        if (onlyLastValue) url.searchParams.append('lastValue', 'true')

        const readingsDTOs = await makeRequest<DatiSensoreDinamicoDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG] },
            ...options,
        })
        const readings = mapToDynamicSensorReadings(readingsDTOs)
        return readings
    },
    getStaticSensorReadings: async (plcId, sensorId, params, options) => {
        const { from, to, strategy, onlyLastValue } = params || {}
        const url = new URL(`${PLC_URL}/${plcId.toString()}/SensoreStatico/${sensorId.toString()}`)
        if (from) url.searchParams.append('timestampFrom', from)
        if (to) url.searchParams.append('timestampTo', to)
        if (strategy) url.searchParams.append('aggregationStrategy', strategy.toString())
        if (onlyLastValue) url.searchParams.append('lastValue', 'true')

        const readingsDTOs = await makeRequest<DatiSensoreStaticoDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG] },
            ...options,
        })
        const readings = mapToStaticSensorReadings(readingsDTOs)
        return readings
    },
    getDigitalSensorReadings: async (plcId, sensorId, params, options) => {
        const { from, to, strategy, onlyLastValue } = params || {}
        const url = new URL(`${PLC_URL}/${plcId.toString()}/SensoreDigitale/${sensorId.toString()}`)
        if (from) url.searchParams.append('timestampFrom', from)
        if (to) url.searchParams.append('timestampTo', to)
        if (strategy) url.searchParams.append('aggregationStrategy', strategy.toString())
        if (onlyLastValue) url.searchParams.append('lastValue', 'true')

        const readingsDTOs = await makeRequest<DatiSensoreStaticoDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG] },
            ...options,
        })
        const readings = mapToStaticSensorReadings(readingsDTOs)
        return readings
    },
    getNewPhoto: async (plcId, options) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Foto/Scatto`)
        const fotoDTO = await makeRequest<FotoDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG, PHOTO_TAG] },
            cache: 'no-store',
            ...options,
        })
        const photo = mapToPhoto(fotoDTO)
        return photo
    },
    getLastPhoto: async (plcId, options) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Foto/UltimaAcquisizione`)
        const fotoDTO = await makeRequest<FotoDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG, PHOTO_TAG] },
            ...options,
        })
        const photo = mapToPhoto(fotoDTO)
        return photo
    },
    getManyPhotos: async (plcId, itemsPerPage, params, options) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Foto/Pagination`)
        if (itemsPerPage) url.searchParams.append('PageSize', itemsPerPage)
        if (params?.page) url.searchParams.append('PageIndex', params.page)
        if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
        if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))
        if (params?.timestampFrom) url.searchParams.append('TimestampFrom', params.timestampFrom)
        if (params?.timestampTo) url.searchParams.append('TimestampTo', params.timestampTo)

        if (params?.search) url.searchParams.append('Filters[search]', params.search)

        const fotoPaginationDTO = await makeRequest<FotoPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG, PHOTO_TAG], revalidate: 300 },
            ...options,
        })
        const photosWithPagination = mapToPhotosWithPagination(fotoPaginationDTO)
        return photosWithPagination
    },
    getDevices: async (plcId, options) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Devices`)
        const devicesDTO = await makeRequest<DevicesDTO>(url, {
            method: 'GET',
            next: { tags: [PLC_TAG] },
            ...options,
        })
        const devices = mapToDevicesDTO(devicesDTO)
        return devices
    },
    putDevice: async (plcId, deviceId, command, options) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Devices/${deviceId.toString()}`)
        const deviceDTO = await makeRequest<DeviceDTO>(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ command }),
            ...options,
        })
        const device = mapToDeviceDTO(deviceDTO)
        return device
    },
})

export { PHOTO_TAG, PLC_TAG, plcCRUD, type PLCCrudType, type PlcParams as PLCGetOptions }
